// src/pages/Works.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebase";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";

/** Utilitaires */
const isVideo = (m) => m?.type === "video" || (typeof m?.url === "string" && /\.(mp4|webm|ogg)(\?|#|$)/i.test(m.url));
const isImage = (m) => m?.type === "image" || (typeof m?.url === "string" && /\.(png|jpe?g|gif|webp|svg)(\?|#|$)/i.test(m.url));

const normalizeGallery = (w) => {
  // Priorité cover + gallery ; fallback imageUrl legacy
  if (Array.isArray(w.gallery) && w.gallery.length) return w.gallery;
  if (w.cover) return [{ type: "image", url: w.cover }];
  if (w.imageUrl) return [{ type: "image", url: w.imageUrl }];
  return [];
};

const parseDate = (d, createdAt) => {
  // d attendu: "YYYY-MM" ou "YYYY-MM-DD"
  if (typeof d === "string" && d.trim()) {
    const val = d.length === 7 ? `${d}-01` : d; // YYYY-MM -> 1er du mois
    const t = Date.parse(val);
    if (!Number.isNaN(t)) return new Date(t);
  }
  // fallback createdAt (Timestamp Firestore) ou 0
  if (createdAt && typeof createdAt.seconds === "number") {
    return new Date(createdAt.seconds * 1000);
  }
  return new Date(0);
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return d.toLocaleDateString("fr-FR", { year: "numeric", month: "short" });
  } catch {
    return String(d);
  }
};

export default function Works() {
  const { dept } = useParams(); // "medical" | "industrial"
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Chargement
  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, "works"),
          where("department", "==", (dept || "medical").toLowerCase()),
          orderBy("createdAt", "desc") // tri primaire stable; on affinera côté client
        );
        const snap = await getDocs(q);
        if (!mounted) return;
        setItems(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [dept]);

  // Tri final par date métier (string) puis createdAt
  const works = useMemo(() => {
    return [...items].sort((a, b) => {
      const da = parseDate(a.date, a.createdAt);
      const dbb = parseDate(b.date, b.createdAt);
      return dbb - da; // desc
    });
  }, [items]);

  // Lightbox
  const [lightbox, setLightbox] = useState({ open: false, wi: 0, mi: 0 });
  const openLightbox = (wi, mi = 0) => setLightbox({ open: true, wi, mi });
  const closeLightbox = () => setLightbox({ open: false, wi: 0, mi: 0 });

  const moveMedia = (dir) => {
    if (!lightbox.open) return;
    const g = normalizeGallery(works[lightbox.wi]);
    if (!g.length) return;
    const n = (lightbox.mi + dir + g.length) % g.length;
    setLightbox((s) => ({ ...s, mi: n }));
  };
  const moveWork = (dir) => {
    if (!lightbox.open) return;
    const n = (lightbox.wi + dir + works.length) % works.length;
    const g = normalizeGallery(works[n]);
    setLightbox({ open: true, wi: n, mi: 0 });
    // Si la suivante a 0 média, on la ferme (rare)
    if (!g.length) closeLightbox();
  };

  return (
    <div className="container py-5">
      <div className="d-flex align-items-end justify-content-between mb-3">
        <div>
          <h2 className="fw-bold mb-0">
            Réalisations — {dept === "industrial" ? "Industriel" : "Médical"}
          </h2>
          <small className="text-muted">
            {works.length} résultat{works.length > 1 ? "s" : ""}
          </small>
        </div>
      </div>

      {/* Skeletons */}
      {loading ? (
        <div className="row g-3 mt-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="col-12 col-sm-6 col-lg-4" key={i}>
              <div className="card shadow-sm h-100">
                <div className="ratio ratio-16x9 placeholder" />
                <div className="card-body">
                  <div className="placeholder-glow">
                    <span className="placeholder col-7"></span>
                  </div>
                  <p className="small text-muted placeholder-glow mb-0">
                    <span className="placeholder col-12"></span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : works.length === 0 ? (
        <div className="alert alert-light border mt-3">
          Aucune réalisation pour l’instant.
        </div>
      ) : (
        <div className="row g-3 mt-2">
          {works.map((w, wi) => {
            const gallery = normalizeGallery(w);
            const first = gallery[0] || null;
            const imgs = gallery.filter(isImage).length;
            const vids = gallery.filter(isVideo).length;
            const d = fmtDate(parseDate(w.date, w.createdAt));

            return (
              <div className="col-12 col-sm-6 col-lg-4" key={w.id}>
                <div className="card h-100 shadow-sm">
                  {/* Vignette */}
                  <div
                    className="ratio ratio-16x9 position-relative rounded-top overflow-hidden"
                    style={{ background: "#f8f9fa" }}
                    onClick={() => (first ? openLightbox(wi, 0) : undefined)}
                    role={first ? "button" : "img"}
                    aria-label={first ? `Ouvrir la galerie de ${w.title}` : undefined}
                    title={first ? "Voir la galerie" : undefined}
                  >
                    {first ? (
                      isVideo(first) ? (
                        <video
                          src={first.url}
                          className="w-100 h-100"
                          style={{ objectFit: "cover", background: "#000" }}
                          muted
                          playsInline
                          controls={false}
                        />
                      ) : (
                        <img
                          src={first.url}
                          alt={w.title}
                          className="w-100 h-100"
                          style={{ objectFit: "cover" }}
                          loading="lazy"
                        />
                      )
                    ) : (
                      <div className="d-flex align-items-center justify-content-center text-muted">
                        <small>Visuel indisponible</small>
                      </div>
                    )}

                    {/* Badges overlay */}
                    <div className="position-absolute top-0 start-0 p-2 d-flex align-items-start gap-2">
                      <span className={`badge ${w.department === "industrial" ? "text-bg-secondary" : "text-bg-success"} align-self-start`}>
                        {w.department === "industrial" ? "Industriel" : "Médical"}
                      </span>
                      {d && <span className="badge text-bg-dark align-self-start">{d}</span>}
                    </div>
                    {gallery.length > 0 && (
                      <div className="position-absolute bottom-0 start-0 p-2 d-flex align-items-start">
                        <span className="badge text-bg-light text-dark align-self-start">
                          
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Corps */}
                  <div className="card-body d-flex flex-column">
                    <h6 className="fw-semibold mb-1">{w.title}</h6>
                    <div className="small text-muted mb-2">
                      {w.place || "—"}
                    </div>
                    {w.blurb && (
                      <p className="small text-muted mb-3" style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {w.blurb}
                      </p>
                    )}

                    <div className="mt-auto d-flex justify-content-between align-items-center">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => (gallery.length ? openLightbox(wi, 0) : undefined)}
                        disabled={!gallery.length}
                      >
                        Voir la galerie
                      </button>
                      <small className="text-muted">
                        {d}
                        {w.date && w.place ? " · " : ""}
                        {w.date && w.place ? w.place : ""}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* LIGHTBOX simple (sans dépendance) */}
      {lightbox.open && works[lightbox.wi] && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ background: "rgba(0,0,0,.85)", zIndex: 1080 }}
          role="dialog"
          aria-modal="true"
          aria-label="Visionneuse de médias"
          onClick={closeLightbox}
        >
          <div
            className="container h-100 d-flex flex-column"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center py-2" style={{ color: "#fff" }}>
              <div className="small opacity-75">
                {works[lightbox.wi].title}
              </div>
              <button type="button" className="btn btn-sm btn-light" onClick={closeLightbox}>
                Fermer
              </button>
            </div>

            {/* Media */}
            <div className="flex-grow-1 d-flex align-items-center justify-content-center">
              <div className="w-100" style={{ maxWidth: 1100 }}>
                <div className="ratio ratio-16x9">
                  {(() => {
                    const g = normalizeGallery(works[lightbox.wi]);
                    const m = g[lightbox.mi];
                    if (!m) return null;
                    return isVideo(m) ? (
                      <video
                        src={m.url}
                        className="w-100 h-100"
                        style={{ objectFit: "contain", background: "#000", borderRadius: 8 }}
                        controls
                        autoPlay
                        playsInline
                      />
                    ) : (
                      <img
                        src={m.url}
                        alt=""
                        className="w-100 h-100"
                        style={{ objectFit: "contain", background: "#111", borderRadius: 8 }}
                      />
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="d-flex justify-content-between align-items-center py-2" style={{ color: "#fff" }}>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => moveWork(-1)}>
                  Réalisation précédente
                </button>
                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => moveWork(1)}>
                  Réalisation suivante
                </button>
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-light btn-sm" onClick={() => moveMedia(-1)}>
                  ◀ Média
                </button>
                <button type="button" className="btn btn-light btn-sm" onClick={() => moveMedia(1)}>
                  Média ▶
                </button>
              </div>
            </div>

            {/* Thumbnails row */}
            <div className="pb-3">
              <div className="d-flex gap-2 overflow-auto">
                {normalizeGallery(works[lightbox.wi]).map((m, idx) => (
                  <button
                    key={`${m.url}-${idx}`}
                    className={`border-0 p-0 ${idx === lightbox.mi ? "opacity-100" : "opacity-75"}`}
                    style={{ background: "transparent", outline: "none" }}
                    onClick={() => setLightbox((s) => ({ ...s, mi: idx }))}
                    title={`Aller au média ${idx + 1}`}
                  >
                    <div className="ratio ratio-16x9" style={{ width: 120, borderRadius: 6, overflow: "hidden", border: idx === lightbox.mi ? "2px solid #fff" : "1px solid rgba(255,255,255,.35)" }}>
                      {isVideo(m) ? (
                        <video src={m.url} muted className="w-100 h-100" style={{ objectFit: "cover", background: "#000" }} />
                      ) : (
                        <img src={m.url} alt="" className="w-100 h-100" style={{ objectFit: "cover" }} />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


